'use client';

import { c as _c } from "react/compiler-runtime";
import { useConstant } from '@cc-shopping/hooks/useConstant';
import { FeatureViewed } from '@cc-shopping/platform-analytics/FeatureViewed';
import { FullBleedBox } from '@cc-shopping/ui/primitives/FullBleedBox';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { Box, Typography } from '@mui/material';
import { v4 as uuid } from 'uuid';
import { CardfinderBranchCta } from './CardfinderBranchCta';
import { BRANCH_CONTENT } from './constants';
import { BRANCH_ILLUSTRATIONS_MAP } from './Illustrations';
import { ILLUSTRATION_SIZE } from './Illustrations/constants';
import { SparkleIcon } from './SparkleIcon';
import type { CardFinderBranch } from './types';
type Props = {
  branch: CardFinderBranch;
};
export function CardFinderBranchEntrypoint(t0) {
  const $ = _c(47);
  const {
    branch
  } = t0;
  const content = BRANCH_CONTENT[branch];
  const {
    SVGComponent,
    alt
  } = BRANCH_ILLUSTRATIONS_MAP[branch];
  const activityId = useConstant(_temp);
  let t1;
  if ($[0] !== branch) {
    t1 = {
      branch_variant: branch
    };
    $[0] = branch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingY: {
        xs: "2rem",
        sm: 6
      },
      gap: {
        sm: "2.5rem"
      }
    };
    t3 = <SparkleIcon />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: {
        xs: "none",
        sm: "block"
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== SVGComponent || $[6] !== alt) {
    t5 = <Box component="picture" sx={t4}><SVGComponent width={ILLUSTRATION_SIZE.width} height={ILLUSTRATION_SIZE.height} aria-hidden="true" aria-label={alt} /></Box>;
    $[5] = SVGComponent;
    $[6] = alt;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      xs: "flex",
      md: "contents"
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      display: t6,
      flexDirection: "column",
      gap: {
        xs: 3,
        sm: "2.5rem"
      }
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 1
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      color: "lime.tintLP",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      fontSize: 12
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== content.eyebrow) {
    t10 = <Typography component="h2" variant="text" sx={t9}>{content.eyebrow}</Typography>;
    $[12] = content.eyebrow;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      fontSize: {
        xs: "24px",
        sm: "30px"
      }
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      fontSize: "inherit",
      color: "common.white"
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      fontSize: "inherit",
      color: "lime.tintLP",
      display: {
        lg: "block"
      }
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== content.headlineHighlight) {
    t14 = <Typography component="span" variant="smallHeadline" sx={t13}>{content.headlineHighlight}</Typography>;
    $[17] = content.headlineHighlight;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== content.headlineLead || $[20] !== t14) {
    t15 = <Box sx={t11}><Typography component="p" variant="smallHeadline" id="branch-entrypoint-heading" sx={t12}>{content.headlineLead}{" "}{t14}</Typography></Box>;
    $[19] = content.headlineLead;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t10 || $[23] !== t15) {
    t16 = <Box sx={t8}>{t10}{t15}</Box>;
    $[22] = t10;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      borderRadius: "4px",
      width: {
        md: "260px"
      },
      position: "relative"
    };
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== activityId || $[27] !== branch) {
    t18 = <CardfinderBranchCta activityId={activityId} branch={branch} />;
    $[26] = activityId;
    $[27] = branch;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mt: 2
    };
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <BoltOutlinedIcon sx={{
      fontSize: 18,
      color: "lime.tintLP"
    }} aria-hidden={true} />;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      color: "common.white",
      fontSize: 12
    };
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== content.footnote) {
    t22 = <Box sx={t19}>{t20}<Typography variant="text" sx={t21}>{content.footnote}</Typography></Box>;
    $[32] = content.footnote;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== t18 || $[35] !== t22) {
    t23 = <Box sx={t17}>{t18}{t22}</Box>;
    $[34] = t18;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t16 || $[38] !== t23) {
    t24 = <Box sx={t7}>{t16}{t23}</Box>;
    $[37] = t16;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== t24 || $[41] !== t5) {
    t25 = <FullBleedBox component="aside" aria-labelledby="branch-entrypoint-heading" data-testid="cardfinder_below_summary_table" background="linear-gradient(115deg, #015538 8.49%, #006642 61.88%, #008254 91.51%)" sx={t2}>{t3}{t5}{t24}</FullBleedBox>;
    $[40] = t24;
    $[41] = t5;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== activityId || $[44] !== t1 || $[45] !== t25) {
    t26 = <FeatureViewed entityName="cc_recommend_flow" entitySection="ru_cardfinder_entry" impressionPosition={0} activityId={activityId} additionalAttributes={t1}>{t25}</FeatureViewed>;
    $[43] = activityId;
    $[44] = t1;
    $[45] = t25;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  return t26;
}
function _temp() {
  return uuid();
}